import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LandingHeader from '../components/LandingHeader';
import LandingFooter from '../components/LandingFooter';
import LandingSplash from '../components/brand/LandingSplash';
import { getLandingPageSettingsPublic } from '../services/landingPageSettings';

const DEFAULT_PALETTE = {
  primary: '#02142C',
  accent: '#FF7300',
  onPrimary: '#FFFFFF',
  background: '#FFFFFF',
  text: '#02142C'
};
const DEFAULT_CONTACT = {
  address: '',
  phone: '',
  email: ''
};

const PublicLayout = () => {
  const location = useLocation();
  const navLinks = useSelector((state) => state.ui?.navLinks || []);
  const [palette, setPalette] = useState(DEFAULT_PALETTE);
  const [contact, setContact] = useState(DEFAULT_CONTACT);
  const [logoUrl, setLogoUrl] = useState('');
  const [logoBackground, setLogoBackground] = useState('');
  const [logoTransparent, setLogoTransparent] = useState(false);
  const [logoWidth, setLogoWidth] = useState(40);
  const [logoHeight, setLogoHeight] = useState(40);
  const [landingLoaded, setLandingLoaded] = useState(false);
  const apiBase = import.meta.env.VITE_API_URL || '';

  const resolveAssetUrl = useMemo(() => {
    const baseUrl = apiBase || (typeof window !== 'undefined' ? window.location.origin : '');
    let root = baseUrl;
    try {
      const parsed = new URL(baseUrl);
      root = parsed.origin + parsed.pathname.replace(/\/$/, '');
    } catch (e) {
      root = baseUrl;
    }
    if (/\/api\/v\d+$/i.test(root)) {
      root = root.replace(/\/api\/v\d+$/i, '');
    }
    return (url) => {
      if (!url) return '';
      if (/^https?:\/\//i.test(url)) return url;
      const trimmed = url.startsWith('/') ? url : `/${url}`;
      return `${root}${trimmed}`;
    };
  }, [apiBase]);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      try {
        const data = await getLandingPageSettingsPublic();
        if (!active) return;
        setPalette({ ...DEFAULT_PALETTE, ...(data?.palette || {}) });
        setContact({ ...DEFAULT_CONTACT, ...(data?.contact || {}) });
        setLogoUrl(resolveAssetUrl(data?.logo?.url || ''));
        setLogoBackground(data?.logo?.background || '');
        setLogoTransparent(Boolean(data?.logo?.backgroundTransparent));
        setLogoWidth(data?.logo?.widthPx ?? 40);
        setLogoHeight(data?.logo?.heightPx ?? 40);
        setLandingLoaded(true);
      } catch {
        if (!active) return;
        setPalette(DEFAULT_PALETTE);
        setContact(DEFAULT_CONTACT);
        setLogoUrl('');
        setLogoBackground('');
        setLogoTransparent(false);
        setLogoWidth(40);
        setLogoHeight(40);
        setLandingLoaded(true);
      }
    };
    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  const layoutStyle = useMemo(() => {
    return {
      minHeight: '100vh',
      background: '#ffffff',
      color: palette.text || '#0F172A'
    };
  }, [palette]);

  return (
    <div className="d-flex flex-column min-vh-100 position-relative" style={layoutStyle}>
      {location.pathname === '/' && <LandingSplash />}
      {landingLoaded && (
        <LandingHeader
          links={navLinks}
          palette={palette}
          logoUrl={logoUrl}
          logoBackground={logoBackground || palette.background}
          logoTransparent={logoTransparent}
          logoWidth={logoWidth}
          logoHeight={logoHeight}
        />
      )}
      <main className="app-page-content flex-grow-1" style={{ paddingTop: 90 }}>
        <Outlet />
      </main>
      {landingLoaded && (
        <LandingFooter
          palette={palette}
          contact={contact}
          logoUrl={logoUrl}
          logoBackground={logoBackground || palette.background}
          logoTransparent={logoTransparent}
          logoWidth={logoWidth}
          logoHeight={logoHeight}
          tintLogoToOnPrimary
        />
      )}
    </div>
  );
};

export default PublicLayout;
